import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { PdfViewerDownloadButton } from "@src/appV2/PdfViewer/PdfViewerDownloadButton";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { usePdfViewerContext } from "./context";

export interface PdfViewerToolbarActions {
  showPageInfo?: boolean;
  showZoom?: boolean;
  showDownload?: boolean;
}

const DEFAULT_TOOLBAR_ACTIONS: Required<PdfViewerToolbarActions> = {
  showPageInfo: true,
  showZoom: false,
  showDownload: true,
};

interface PdfViewerToolbarProps {
  actions?: PdfViewerToolbarActions;
}

export function PdfViewerToolbar(props: PdfViewerToolbarProps) {
  const { actions } = props;
  const { showPageInfo, showZoom, showDownload } = { ...DEFAULT_TOOLBAR_ACTIONS, ...actions };
  const { currentPage, numberOfPages, zoomLevel, zoomIn, zoomOut } = usePdfViewerContext();

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      overflow="auto"
      spacing={1}
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        borderBottom: (theme) => `1px solid ${theme.palette.grey[400]}`,
        flexShrink: 0,
        px: 3,
        py: 0,
      }}
    >
      {showPageInfo && (
        <Text sx={{ padding: 1 }}>
          {isDefined(numberOfPages) && `Page ${currentPage} of ${numberOfPages}`}
        </Text>
      )}

      {showZoom && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            aria-label="Zoom out"
            iconType="minus"
            size="xSmall"
            variant="outlined"
            onClick={zoomOut}
          />
          <Text variant="body2" sx={{ minWidth: 40, textAlign: "center" }}>
            {Math.round(zoomLevel * 100)}%
          </Text>
          <IconButton
            aria-label="Zoom in"
            iconType="plus"
            size="xSmall"
            variant="outlined"
            onClick={zoomIn}
          />
        </Stack>
      )}

      {showDownload && <PdfViewerDownloadButton />}
    </Stack>
  );
}
